import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { Search, Filter, Download, ChevronDown, ChevronRight, RotateCcw, ExternalLink } from 'lucide-react'

type Source = 'all' | 'hfri' | 'internal' | 'plutus'

const SOURCE_OPTIONS: { key: Source; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'hfri', label: 'HFR' },
  { key: 'internal', label: 'Internal' },
  { key: 'plutus', label: 'Plutus' },
]

/** Format AUM (stored in $M) as compact string: $1.2B or $350M */
function formatAum(value: number | null | undefined): string {
  if (value == null) return '-'
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}B`
  if (value >= 1) return `$${Math.round(value)}M`
  return `$${(value * 1000).toFixed(0)}K`
}

function formatFee(value: number | null | undefined): string {
  if (value == null) return '-'
  return `${value.toFixed(1)}%`
}

type SortField = 'name' | 'firm' | 'fund_aum' | 'firm_aum' | 'inception_date' | 'management_fee' | 'performance_fee' | 'main_strategy'
type SortDir = 'asc' | 'desc'

export default function Screening() {
  const navigate = useNavigate()

  // Filter state
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [minFundAum, setMinFundAum] = useState(0)
  const [maxFundAum, setMaxFundAum] = useState<number | null>(null)
  const [minFirmAum, setMinFirmAum] = useState(0)
  const [maxFirmAum, setMaxFirmAum] = useState<number | null>(null)
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null)
  const [selectedSubStrategy, setSelectedSubStrategy] = useState<string | null>(null)
  const [selectedSource, setSelectedSource] = useState<Source>('all')
  const [inceptionFrom, setInceptionFrom] = useState<number | null>(null)
  const [inceptionTo, setInceptionTo] = useState<number | null>(null)
  const [minSharpe, setMinSharpe] = useState<number | null>(null)
  const [showFilters, setShowFilters] = useState(true)

  // Table state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data: strategies } = useQuery({
    queryKey: ['strategies', selectedSource],
    queryFn: () => api.get(`/screening/strategies?source=${selectedSource}`).then((r) => r.data),
  })

  // Build sub-strategy list from selected main strategy
  const subStrategies = useMemo(() => {
    if (!selectedStrategy || !strategies) return []
    const match = strategies.find((s: any) => s.main_strategy === selectedStrategy)
    return match?.sub_strategies || []
  }, [strategies, selectedStrategy])

  const { data: funds, isLoading } = useQuery({
    queryKey: ['screening-funds', searchTerm, minFundAum, maxFundAum, minFirmAum, maxFirmAum, selectedStrategy, selectedSubStrategy, selectedSource, inceptionFrom, inceptionTo, minSharpe],
    queryFn: () => {
      const params = new URLSearchParams()
      if (searchTerm) params.append('search_term', searchTerm)
      if (minFundAum > 0) params.append('min_fund_aum', minFundAum.toString())
      if (maxFundAum) params.append('max_fund_aum', maxFundAum.toString())
      if (minFirmAum > 0) params.append('min_firm_aum', minFirmAum.toString())
      if (maxFirmAum) params.append('max_firm_aum', maxFirmAum.toString())
      if (selectedStrategy) params.append('main_strategy', selectedStrategy)
      if (selectedSubStrategy) params.append('sub_strategy', selectedSubStrategy)
      if (selectedSource !== 'all') params.append('source', selectedSource)
      if (inceptionFrom) params.append('inception_year_from', inceptionFrom.toString())
      if (inceptionTo) params.append('inception_year_to', inceptionTo.toString())
      if (minSharpe != null) params.append('min_sharpe', minSharpe.toString())
      params.append('limit', '500')
      return api.get(`/screening/funds?${params}`).then((r) => r.data)
    },
  })

  // Sort funds
  const sortedFunds = useMemo(() => {
    if (!funds) return []
    return [...funds].sort((a: any, b: any) => {
      let va = a[sortField]
      let vb = b[sortField]
      if (va == null) va = sortDir === 'asc' ? Infinity : -Infinity
      if (vb == null) vb = sortDir === 'asc' ? Infinity : -Infinity
      if (typeof va === 'string') va = va.toLowerCase()
      if (typeof vb === 'string') vb = vb.toLowerCase()
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [funds, sortField, sortDir])

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir(field === 'name' || field === 'firm' || field === 'main_strategy' ? 'asc' : 'desc')
    }
  }

  function toggleRow(key: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function resetFilters() {
    setSearchInput('')
    setSearchTerm('')
    setMinFundAum(0)
    setMaxFundAum(null)
    setMinFirmAum(0)
    setMaxFirmAum(null)
    setSelectedStrategy(null)
    setSelectedSubStrategy(null)
    setInceptionFrom(null)
    setInceptionTo(null)
    setMinSharpe(null)
    setExpandedRows(new Set())
  }

  function exportCsv() {
    if (!sortedFunds.length) return
    const cols = ['name', 'firm', 'source', 'main_strategy', 'sub_strategy', 'fund_aum', 'firm_aum',
      'inception_date', 'management_fee', 'performance_fee', 'lockup_months', 'redemption_notice_days',
      'principals', 'city', 'country', 'structure', 'domicile', 'leverage', 'strategy_description',
      'high_watermark', 'hurdle_rate', 'open_for_investment', 'subscriptions', 'redemptions',
      'minimum_investment', 'returns_denomination', 'regional_focus',
      'administrator', 'auditor', 'prime_broker', 'custodian', 'legal_adviser']
    const header = cols.join(',')
    const rows = sortedFunds.map((f: any) =>
      cols.map((c) => {
        const v = f[c]
        if (v == null) return ''
        const s = String(v).replace(/"/g, '""')
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s}"` : s
      }).join(',')
    )
    const csv = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `screening_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const SortHeader = ({ field, label, align = 'left' }: { field: SortField; label: string; align?: string }) => (
    <th
      className={`px-3 py-2 font-medium cursor-pointer hover:bg-muted/80 select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        {sortField === field && <span className="text-[9px]">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
      </span>
    </th>
  )

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fund Screening</h1>
          <p className="text-sm text-muted-foreground">Search and filter across {funds?.length ?? '...'} hedge funds</p>
        </div>
        <div className="flex gap-1">
          {SOURCE_OPTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => { setSelectedSource(s.key); setSelectedStrategy(null); setSelectedSubStrategy(null) }}
              className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                selectedSource === s.key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-input hover:bg-accent'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-3">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 text-sm font-medium hover:text-primary">
            <Filter className="h-4 w-4 text-muted-foreground" />
            Filters
            {showFilters ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
          <button onClick={resetFilters} className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-primary border border-input rounded-md hover:bg-muted">
            <RotateCcw className="h-3 w-3" />
            Reset
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {/* Search */}
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Fund or firm name..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>

            {/* Main Strategy */}
            <div>
              <label className="block text-xs font-medium mb-1">Strategy</label>
              <select
                value={selectedStrategy || ''}
                onChange={(e) => { setSelectedStrategy(e.target.value || null); setSelectedSubStrategy(null) }}
                className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">All strategies</option>
                {strategies?.map((s: any) => (
                  <option key={s.main_strategy} value={s.main_strategy}>
                    {s.main_strategy} ({s.fund_count})
                  </option>
                ))}
              </select>
            </div>

            {/* Sub Strategy */}
            <div>
              <label className="block text-xs font-medium mb-1">Sub-Strategy</label>
              <select
                value={selectedSubStrategy || ''}
                onChange={(e) => setSelectedSubStrategy(e.target.value || null)}
                disabled={!selectedStrategy || subStrategies.length === 0}
                className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              >
                <option value="">All</option>
                {subStrategies.map((s: string) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Fund AUM */}
            <div>
              <label className="block text-xs font-medium mb-1">Fund AUM Min ($M)</label>
              <input
                type="number"
                value={minFundAum || ''}
                onChange={(e) => setMinFundAum(Number(e.target.value) || 0)}
                min={0}
                placeholder="0"
                className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Fund AUM Max ($M)</label>
              <input
                type="number"
                value={maxFundAum ?? ''}
                onChange={(e) => setMaxFundAum(e.target.value ? Number(e.target.value) : null)}
                min={0}
                placeholder="No limit"
                className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Firm AUM */}
            <div>
              <label className="block text-xs font-medium mb-1">Firm AUM Min ($M)</label>
              <input
                type="number"
                value={minFirmAum || ''}
                onChange={(e) => setMinFirmAum(Number(e.target.value) || 0)}
                min={0}
                placeholder="0"
                className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Firm AUM Max ($M)</label>
              <input
                type="number"
                value={maxFirmAum ?? ''}
                onChange={(e) => setMaxFirmAum(e.target.value ? Number(e.target.value) : null)}
                min={0}
                placeholder="No limit"
                className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Inception Year */}
            <div>
              <label className="block text-xs font-medium mb-1">Inception From</label>
              <input
                type="number"
                value={inceptionFrom ?? ''}
                onChange={(e) => setInceptionFrom(e.target.value ? Number(e.target.value) : null)}
                min={1950}
                max={new Date().getFullYear()}
                placeholder="Any"
                className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Inception To</label>
              <input
                type="number"
                value={inceptionTo ?? ''}
                onChange={(e) => setInceptionTo(e.target.value ? Number(e.target.value) : null)}
                min={1950}
                max={new Date().getFullYear()}
                placeholder="Any"
                className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Min Sharpe */}
            <div>
              <label className="block text-xs font-medium mb-1">Min Sharpe Ratio</label>
              <input
                type="number"
                value={minSharpe ?? ''}
                onChange={(e) => setMinSharpe(e.target.value ? Number(e.target.value) : null)}
                min={0}
                max={10}
                step={0.1}
                placeholder="Any"
                className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
          <span className="text-sm font-medium">
            {isLoading ? 'Loading...' : `${sortedFunds.length} funds found`}
          </span>
          <button
            onClick={exportCsv}
            disabled={!sortedFunds.length}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-input rounded-md hover:bg-muted disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>

        {isLoading ? (
          <div className="p-8 text-center"><span className="spinner mx-auto" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider">
                  <th className="w-8 px-1 py-2" />
                  <SortHeader field="name" label="Fund Name" />
                  <th className="text-left px-3 py-2 font-medium">Src</th>
                  <SortHeader field="firm" label="Firm" />
                  <SortHeader field="main_strategy" label="Strategy" />
                  <th className="text-left px-3 py-2 font-medium">Sub-Strategy</th>
                  <SortHeader field="fund_aum" label="Fund AUM" align="right" />
                  <SortHeader field="firm_aum" label="Firm AUM" align="right" />
                  <SortHeader field="inception_date" label="Inception" />
                  <SortHeader field="management_fee" label="Mgmt Fee" align="right" />
                  <SortHeader field="performance_fee" label="Perf Fee" align="right" />
                  <th className="text-left px-3 py-2 font-medium">Country</th>
                  <th className="text-left px-3 py-2 font-medium">Structure</th>
                </tr>
              </thead>
              <tbody>
                {sortedFunds.map((fund: any) => {
                  const key = `${fund.source}-${fund.fund_id}`
                  const isExpanded = expandedRows.has(key)
                  return (
                    <>
                      <tr
                        key={key}
                        className="border-b border-border/50 hover:bg-primary/5 transition-colors"
                      >
                        <td className="px-1 py-2 text-center">
                          <button onClick={() => toggleRow(key)} className="p-0.5 hover:bg-muted rounded">
                            {isExpanded
                              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                          </button>
                        </td>
                        <td
                          className="px-3 py-2 font-medium cursor-pointer hover:text-primary truncate max-w-[280px]"
                          onClick={() => navigate(`/screening/${fund.fund_id}?source=${fund.source}`)}
                          title={fund.name}
                        >
                          {fund.name}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            fund.source === 'hfri' ? 'bg-purple-100 text-purple-700' :
                            fund.source === 'plutus' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {fund.source === 'hfri' ? 'HFR' :
                             fund.source === 'plutus' ? 'PLU' : 'INT'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground truncate max-w-[200px]" title={fund.firm}>{fund.firm || '-'}</td>
                        <td className="px-3 py-2 truncate max-w-[140px]" title={fund.main_strategy}>{fund.main_strategy || '-'}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[140px]" title={fund.sub_strategy}>{fund.sub_strategy || '-'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{formatAum(fund.fund_aum)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{formatAum(fund.firm_aum)}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{formatDate(fund.inception_date)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{formatFee(fund.management_fee)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{formatFee(fund.performance_fee)}</td>
                        <td className="px-3 py-2 text-xs">{fund.country || '-'}</td>
                        <td className="px-3 py-2 text-xs">{fund.structure || '-'}</td>
                      </tr>

                      {/* Expanded Detail Row */}
                      {isExpanded && (
                        <tr key={`${key}-detail`} className="bg-muted/30 border-b border-border">
                          <td colSpan={13} className="px-6 py-4">
                            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                              {/* Fund Info */}
                              <div>
                                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Fund Info</h4>
                                <dl className="space-y-1 text-xs">
                                  <DetailItem label="Principals" value={fund.principals} />
                                  <DetailItem label="City" value={fund.city} />
                                  <DetailItem label="Country" value={fund.country} />
                                  <DetailItem label="Domicile" value={fund.domicile} />
                                  <DetailItem label="Structure" value={fund.structure} />
                                  <DetailItem label="Regional Focus" value={fund.regional_focus} />
                                  <DetailItem label="Returns Currency" value={fund.returns_denomination} />
                                  <DetailItem label="Open for Investment" value={fund.open_for_investment} />
                                </dl>
                              </div>

                              {/* AUM & Strategy */}
                              <div>
                                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">AUM & Strategy</h4>
                                <dl className="space-y-1 text-xs">
                                  <DetailItem label="Fund AUM" value={formatAum(fund.fund_aum)} />
                                  <DetailItem label="Fund AUM As Of" value={fund.fund_aum_as_of ? formatDate(fund.fund_aum_as_of) : null} />
                                  <DetailItem label="Firm AUM" value={formatAum(fund.firm_aum)} />
                                  <DetailItem label="Firm AUM As Of" value={fund.firm_aum_as_of ? formatDate(fund.firm_aum_as_of) : null} />
                                  <DetailItem label="Leverage" value={fund.leverage} />
                                  <DetailItem label="Min Investment" value={fund.minimum_investment != null ? formatAum(fund.minimum_investment / 1_000_000) : null} />
                                </dl>
                              </div>

                              {/* Fees & Terms */}
                              <div>
                                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Fees & Terms</h4>
                                <dl className="space-y-1 text-xs">
                                  <DetailItem label="Management Fee" value={formatFee(fund.management_fee)} />
                                  <DetailItem label="Performance Fee" value={formatFee(fund.performance_fee)} />
                                  <DetailItem label="High Watermark" value={fund.high_watermark} />
                                  <DetailItem label="Hurdle Rate" value={fund.hurdle_rate} />
                                  <DetailItem label="Lockup" value={fund.lockup_months != null ? `${fund.lockup_months} months` : null} />
                                  <DetailItem label="Redemption Notice" value={fund.redemption_notice_days != null ? `${fund.redemption_notice_days} days` : null} />
                                  <DetailItem label="Subscriptions" value={fund.subscriptions} />
                                  <DetailItem label="Redemptions" value={fund.redemptions} />
                                </dl>
                              </div>

                              {/* Service Providers */}
                              <div>
                                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Service Providers</h4>
                                <dl className="space-y-1 text-xs">
                                  <DetailItem label="Administrator" value={fund.administrator} />
                                  <DetailItem label="Auditor" value={fund.auditor} />
                                  <DetailItem label="Prime Broker" value={fund.prime_broker} />
                                  <DetailItem label="Custodian" value={fund.custodian} />
                                  <DetailItem label="Legal Adviser" value={fund.legal_adviser} />
                                </dl>
                              </div>
                            </div>

                            {/* Strategy Description */}
                            {fund.strategy_description && (
                              <div className="mt-3 pt-3 border-t border-border/50">
                                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">Strategy Description</h4>
                                <p className="text-xs text-muted-foreground leading-relaxed">{fund.strategy_description}</p>
                              </div>
                            )}

                            {/* Action */}
                            <div className="mt-3 pt-3 border-t border-border/50 flex justify-end">
                              <button
                                onClick={() => navigate(`/screening/${fund.fund_id}?source=${fund.source}`)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Full Analysis
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
            </table>
            {sortedFunds.length === 0 && !isLoading && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No funds match your criteria. Try adjusting the filters.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function DetailItem({ label, value }: { label: string; value: string | null | undefined }) {
  const display = value == null || value === '-' || value === '' ? '-' : value
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-right ml-2 max-w-[180px] truncate" title={display}>{display}</dd>
    </div>
  )
}
